import api from './client';
import type { Campaign, CreateCampaignInput, UpdateCampaignInput } from './types';

export interface GetCampaignsOpts {
  status?: string;
  includeArchived?: boolean;
}

/**
 * Get campaigns. By default archived campaigns are hidden.
 * Pass `{ status }` to filter (e.g. 'archived', 'active'), or
 * `{ includeArchived: true }` to fetch active + archived (e.g. for name lookups).
 * Called with no arguments as `queryFn: getCampaigns` for React Query compatibility.
 */
export async function getCampaigns(): Promise<Campaign[]>;
export async function getCampaigns(opts: GetCampaignsOpts): Promise<Campaign[]>;
export async function getCampaigns(opts?: GetCampaignsOpts): Promise<Campaign[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.includeArchived) params.set('include_archived', '1');
  const qs = params.toString();
  return api.get<Campaign[]>(`/admin/campaigns${qs ? `?${qs}` : ''}`);
}

/**
 * Get a single campaign by ID
 */
export async function getCampaign(id: string): Promise<Campaign> {
  return api.get<Campaign>(`/admin/campaigns/${id}`);
}

/**
 * Create a new campaign
 */
export async function createCampaign(data: CreateCampaignInput): Promise<Campaign> {
  return api.post<Campaign>('/admin/campaigns', data);
}

/**
 * Update an existing campaign
 */
export async function updateCampaign(id: string, data: UpdateCampaignInput): Promise<Campaign> {
  return api.put<Campaign>(`/admin/campaigns/${id}`, data);
}

/**
 * Delete a campaign
 */
export async function deleteCampaign(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/campaigns/${id}`);
}

/**
 * Archive a campaign (one-way). Cascades to the campaign's ads. Archived campaigns
 * are hidden from the default list, never serve, and stay available for statistics.
 */
export async function archiveCampaign(
  id: string
): Promise<{ archived: boolean; ads_archived: number }> {
  return api.post<{ archived: boolean; ads_archived: number }>(
    `/admin/campaigns/${id}/archive`,
    {}
  );
}
